import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import { CookieConsent } from "@/components/CookieConsent";
import { WhatsAppButton } from "@/components/chat/WhatsAppButton";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { prefetchFullCatalog } from "@/lib/shopify";

// Lazy load non-critical routes for code splitting
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const BrandsIndex = lazy(() => import("./pages/BrandsIndex"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const BusinessAuth = lazy(() => import("./pages/BusinessAuth"));
const BusinessDashboard = lazy(() => import("./pages/BusinessDashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const PurchaseTerms = lazy(() => import("./pages/PurchaseTerms"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const ParameterIndex = lazy(() => import("./pages/ParameterIndex"));
const ParameterDetail = lazy(() => import("./pages/ParameterDetail"));

const queryClient = new QueryClient();

// Warm the product catalog cache shortly after first paint so the first
// category click on /shop renders instantly from memory.
const CatalogPrefetch = () => {
  useEffect(() => {
    const start = () => prefetchFullCatalog();
    const timer = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(start, { timeout: 5000 });
      } else {
        start();
      }
    }, 2000);
    return () => window.clearTimeout(timer);
  }, []);
  return null;
};

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Language routes wrapper component
const LanguageRoutes = () => (
  <MaintenanceGate>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/brands" element={<BrandsIndex />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/brand/:brandSlug" element={<BrandPage />} />
        {/* Parameter pages: finishes, materials, sizes */}
        <Route path="/finishes" element={<ParameterIndex kind="finish" />} />
        <Route path="/finish/:slug" element={<ParameterDetail kind="finish" />} />
        <Route path="/materials" element={<ParameterIndex kind="material" />} />
        <Route path="/material/:slug" element={<ParameterDetail kind="material" />} />
        <Route path="/sizes" element={<ParameterIndex kind="size" />} />
        <Route path="/size/:slug" element={<ParameterDetail kind="size" />} />
        <Route path="/product/:handle" element={<ProductDetail />} />
        <Route path="/product/:handle/:variantSlug" element={<ProductDetail />} />

        {/* Legacy product URL with the /product/ segment in the middle */}
        <Route path="/:categorySlug/:subcategorySlug/product/:handle" element={<ProductDetail />} />

        <Route path="/cookies-policy" element={<CookiesPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/purchase-terms" element={<PurchaseTerms />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/business/auth" element={<BusinessAuth />} />
        <Route path="/business/dashboard" element={<BusinessDashboard />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* SEO-friendly catalog URLs: /bathroom, /bathroom/basin-faucets,
            /bathroom/basin-faucets/:handle and /.../:handle/:variantSlug */}
        <Route path="/:categorySlug" element={<Shop />} />
        <Route path="/:categorySlug/:subcategorySlug" element={<Shop />} />
        <Route path="/:categorySlug/:subcategorySlug/:handle" element={<ProductDetail />} />
        <Route path="/:categorySlug/:subcategorySlug/:handle/:variantSlug" element={<ProductDetail />} />

        <Route path="*" element={<NotFound />} />

      </Routes>
    </Suspense>
  </MaintenanceGate>
);


// App component with language support
const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner position="bottom-center" duration={2000} />
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <Routes>
              {/* Root redirect to default language */}
              <Route path="/" element={<Navigate to="/lv" replace />} />
              
              {/* Language-prefixed routes */}
              <Route path="/en/*" element={<LanguageRoutes />} />
              <Route path="/lv/*" element={<LanguageRoutes />} />
              <Route path="/ru/*" element={<LanguageRoutes />} />
              
              {/* Admin routes without language prefix */}
              <Route path="/admin" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              } />
              <Route path="/admin/*" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              } />
              
              
              {/* Catch-all redirect to Latvian version */}
              <Route path="*" element={<Navigate to="/lv" replace />} />
            </Routes>
            <CookieConsent />
            <CatalogPrefetch />
            <WhatsAppButton />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
